<template>
  <section
    :class="[
      { sticky },
      {
        'overview-analytics-container--with-tab-actions': showOverviewTabActions,
        'overview-analytics-container--with-analytics-exports': showAnalyticsTabActions,
      },
    ]"
    class="overview-analytics-container"
    v-if="filterSelected && filterSelected.company"
  >
    <button
      class="export-button-container"
      @click="print"
      v-if="showPDFExport && !showAnalyticsTabActions"
      :class="{ bounce: isPrinting }"
    >
      <img :src="require('@/assets/performance/pdf-export.svg')" alt="export pdf" />
    </button>
    <div class="analytics-export" v-if="showAnalyticsTabActions" @click.stop>
      <button
        type="button"
        class="analytics-export__trigger"
        :class="{ bounce: isExportingAnything }"
        :title="$t('export')"
        :aria-label="$t('export')"
        :aria-expanded="String(isExportMenuOpen)"
        @click="toggleExportMenu"
      >
        <svg class="analytics-export__icon" viewBox="0 0 24 24" aria-hidden="true">
          <path
            fill="currentColor"
            d="M5 20h14v-2H5v2zM19 9h-4V3H9v6H5l7 7 7-7z"
          />
        </svg>
      </button>
      <div v-if="isExportMenuOpen" class="analytics-export__menu" role="menu">
        <button type="button" role="menuitem" :disabled="isPrinting" @click="runExport('print')">
          {{ $t('er_export_pdf') }}
        </button>
        <button
          v-if="canExportExecutiveReport"
          type="button"
          role="menuitem"
          :disabled="isDownloadingExecutiveReport"
          @click="runExport('executive')"
        >
          {{ $t('er_export_executive') }}
        </button>
        <button type="button" role="menuitem" :disabled="isExportingCsv" @click="runExport('csv')">
          {{ $t('er_export_csv') }}
        </button>
      </div>
    </div>
    <!-- Clustered so customize + fullscreen stay clear of the last tab label. -->
    <div class="dashboard-tab-actions" v-if="showOverviewTabActions">
      <button
        type="button"
        class="dashboard-customize-button"
        v-if="showDashboardCustomizeButton"
        :title="$t('dashboard_customize')"
        :aria-label="$t('dashboard_customize')"
        @click="enterDashboardEditMode"
      >
        <svg class="dashboard-customize-button__icon" viewBox="0 0 24 24" aria-hidden="true">
          <path :d="customizeIcon" fill="currentColor" />
        </svg>
      </button>
      <button
        class="full-screen-button"
        type="button"
        v-if="$route.name === 'overview' && !isFullScreen"
        @click="openFullScreen"
        :title="$t('full_screen')"
        :aria-label="$t('full_screen')"
      >
        <img :src="require('@/assets/full_screen.svg')" alt="" />
      </button>
    </div>
    <div ref="dashboardFullScreenTarget" class="dashboard-fullscreen-wrapper">
      <button
        class="exit-full-screen-button"
        v-if="isFullScreen"
        @click="closeFullScreen"
        type="button"
        :title="$t('exit_full_screen')"
      >
        <img :src="require('@/assets/exit_full_screen.svg')" :alt="$t('exit_full_screen')" />
      </button>
      <Tabs
        ref="dashboardTabs"
        class="dashboard-tabs"
        :class="[
          currentSiteInfo.embedded_link ? 'iframe-tab' : '',
          { 'dashboard-tabs--cleaning-work-plan': $route.name === 'cleaningWorkPlan' },
        ]"
        :isRTL="isRTL"
        v-if="isTabsLoading"
        syncRoute
      >
      <template v-if="$route.params.roomId ? true : false">
        <Tab :label="$t('analytics_log')" :to="`/dashboard/roomAnalyticsLog/${$route.params.roomId}`" :disabled="isLoading">
          <Loading :height="loadingGIFHeight" v-if="isLoading" />
          <ServiceLog
            v-else
            :isPrinting="isPrinting"
            :hideInfo="analyticsHideInfo"
            @donePrinting="isPrinting = false"
          />
        </Tab>
        <!-- <Tab :label="$t('clean_log')" :to="`/dashboard/serviceLog/cleanLog/${$route.params.roomId}`" :disabled="isLoading">
          <Loading :height="loadingGIFHeight" v-if="isLoading" />
          <ServiceLog :tableData="hourTableData" v-else />
        </Tab> -->
        <Tab :label="$t('maintenance_log')" :to="`/dashboard/serviceLog/maintenanceLog/${$route.params.roomId}`" :disabled="isLoading">
          <Loading :height="loadingGIFHeight" v-if="isLoading" />
          <ServiceLog :tableData="hourTableData" v-else />
        </Tab>
        <Tab :label="$t('performance')" :to="`/dashboard/roomAnalytics/${$route.params.roomId}`" :disabled="isLoading">
          <Loading :height="loadingGIFHeight" v-if="isLoading" />
          <Performance v-else />
        </Tab>
        <Tab :label="$t('analytics')" :to="`/dashboard/cleanerArrival/${$route.params.roomId}`" :disabled="isLoading">
          <Loading :height="loadingGIFHeight" v-if="isLoading" />
          <ServiceLog :cleanerArrivals="cleanerArrivals" v-else />
        </Tab>
        <Tab v-if="isExposureShown" :label="$t('exposures_tab')" :to="`/dashboard/roomExposures/${$route.params.roomId}`" :disabled="isLoading">
          <Loading :height="loadingGIFHeight" v-if="isLoading" />
          <ServiceLog :roomExposures="roomExposures" v-else />
        </Tab>
      </template>
      <template v-else>
        <Tab :label="$t('overview')" to="/dashboard" class="overview-tab" exact :disabled="isLoading">
          <Loading :height="loadingGIFHeight" v-if="isLoading || !isOverviewShown" />
          <Overview v-else-if="!isSurveyOverview" :key="`${filterSelected?.company}-${filterSelected?.site}`" />
          <section class="iframe-section" v-else-if="currentSiteInfo.embedded_link">
            <iframe frameborder="0" :src="currentSiteInfo.embedded_link" />
          </section>
          <SurveyOverview :siteType="currentSiteInfo.type" v-else />
        </Tab>
        <Tab v-if="tabs.includes('suggestion')" :label="$t('suggestion')" to="/dashboard/suggestion" :disabled="isLoading">
          <Loading :height="loadingGIFHeight" v-if="isLoading" />
          <Suggestion :hideInfo="analyticsHideInfo" v-else />
        </Tab>
        <Tab v-if="tabs.includes('performance')" :label="$t('performance')" to="/dashboard/performance" :disabled="isLoading">
          <Loading :height="loadingGIFHeight" v-if="isLoading" />
          <Performance v-else />
        </Tab>
        <Tab v-if="tabs.includes('analytics')" :label="$t('analytics')" to="/dashboard/analytics" :disabled="isLoading">
          <Loading :height="loadingGIFHeight" v-if="isLoading" />
          <Analytics
            id="first"
            :isPrinting="isPrinting"
            :hideInfo="analyticsHideInfo"
            @donePrinting="isPrinting = false"
            v-show="$route.name === 'analytics'"
            v-else
          />
        </Tab>
        <Tab v-if="tabs.includes('communication')" :label="$t('communication')" to="/dashboard/communication" :disabled="isLoading">
          <Loading :height="loadingGIFHeight" v-if="isLoading" />
          <Communication v-else-if="$route.name === 'communication'" />
        </Tab>
        <Tab v-if="tabs.includes('cleaningWorkPlan') && loggedUser && loggedUser.level === 'buzzz'" :label="cleaningWorkPlanTabLabel" to="/dashboard/cleaningWorkPlan" :disabled="isLoading">
          <Loading :height="loadingGIFHeight" v-if="isLoading" />
          <CleaningWorkPlan v-else-if="$route.name === 'cleaningWorkPlan'" />
        </Tab>
      </template>
    </Tabs>
    </div>

    <transition name="export-modal-fade">
      <div
        v-if="showExportModal"
        class="export-modal-overlay"
        :class="{ rtl: isRTL }"
        @click.self="cancelExport"
        @wheel.prevent
        @touchmove.prevent
      >
        <div class="export-modal" role="dialog" aria-modal="true" @wheel.stop @touchmove.stop>
          <button type="button" class="export-modal__close" @click="cancelExport" :disabled="isExportingCsv">&times;</button>
          <div class="export-modal__icon">
            <img :src="require('@/assets/performance/csv-export.svg')" alt="" />
          </div>
          <h3 class="export-modal__title">{{ $t('analytics_export_title') }}</h3>
          <p class="export-modal__desc">{{ $t('analytics_export_description') }}</p>
          <div class="export-modal__actions">
            <button
              type="button"
              class="export-modal__btn export-modal__btn--ghost"
              @click="cancelExport"
              :disabled="isExportingCsv"
            >
              {{ $t('cancel') }}
            </button>
            <button
              type="button"
              class="export-modal__btn export-modal__btn--primary"
              @click="confirmExport"
              :disabled="isExportingCsv"
            >
              <span v-if="isExportingCsv" class="export-modal__spinner" />
              {{ isExportingCsv ? $t('loading') : $t('download') }}
            </button>
          </div>
        </div>
      </div>
    </transition>

    <ExecutiveReportExportDialog
      :visible="showExecutiveExportModal"
      :site-id="filterSelected && filterSelected.site"
      :panel-time-from="filterSelected && filterSelected.time ? filterSelected.time.timeFrom : ''"
      :panel-shift-id="singlePanelShiftId"
      :is-r-t-l="isRTL"
      :downloading="isDownloadingExecutiveReport"
      @cancel="cancelExecutiveExport"
      @confirm="confirmExecutiveExport"
    />

    <div
      v-if="isDownloadingExecutiveReport"
      class="er-export-status"
      role="status"
      aria-live="polite"
    >
      <span class="er-export-status__spinner" aria-hidden="true" />
      <span class="er-export-status__copy">
        <span class="er-export-status__title">{{ $t('er_export_status_title') }}</span>
        <span class="er-export-status__detail">{{ executiveExportStatusDetail }}</span>
      </span>
    </div>

    <ExecutiveReportPrint
      v-if="executiveReportPayload"
      class="executive-report-offscreen"
      :report="executiveReportPayload"
      :selected-kpis="exportedExecutiveKpis"
      @progress="onExecutiveReportProgress"
      @done="onExecutiveReportDone"
      @error="onExecutiveReportError"
    />
  </section>
</template>

<script>
import Tabs from '@/modules/common/components/Tabs/Tabs'
import Tab from '@/modules/common/components/Tabs/Tab'
import Loading from '../../common/components/Loading'
import performanceService, { getRoomCleanerArrivals, getRoomExposures, getRoomDataByHour } from '../services/performanceService'
import csvService from '../../common/services/csvService'
import zipService from '../../common/services/zipService'
import Performance from '../components/Performance'
import { mapGetters } from 'vuex'
import { mdiTune } from '@mdi/js'
import { DASHBOARD_CUSTOMIZE_ENABLED } from '../widgets/widgetRegistry'
import Analytics from '../components/Analytics'
import Overview from './Overview'
import SurveyOverview from './SurveyOverview'
import Suggestion from './Suggestion'
import Communication from './Communication'
import ServiceLog from '../components/ServiceLog'
import roiCalculator from '../components/roiCalculator'
import CleaningWorkPlan from '../components/CleaningWorkPlan'
import ExecutiveReportExportDialog from '../components/ExecutiveReportExportDialog'
import ExecutiveReportPrint from '../components/ExecutiveReportPrint'
import { canExportExecutiveReport, getExecutiveReport } from '../services/executiveReportService'
import { Toast } from '@/modules/common/Toast'
import { lockPageScroll, unlockPageScroll } from '@/modules/common/utils/scrollLock'
import {
	buildAnalyticsArrivalQuery,
	normalizeAnalyticsFilter,
	hasServikNameFilter,
	getServikDisplayNames,
} from '../../common/services/filterService'

export default {
  data() {
    return {
      sticky: true,
      loadingGIFHeight: '62vh',
      cleanerArrivals: [],
      roomExposures: [],
      hourTableData: [],
      analyticsHideInfo: [],
      isOverviewShown: true,
      isExposureShown: false,
      tabs: ['overview', 'suggestion', 'analytics', 'performance', 'communication', 'roiCalculator', 'cleaningWorkPlan'],
      isSurveyOverview: false,
      isTabsLoading: false,
      isDownloadingExecutiveReport: false,
      executiveExportProgress: null,
      executiveReportPayload: null,
      exportedExecutiveKpis: [],
      showExecutiveExportModal: false,
      isExportingCsv: false,
      showExportModal: false,
      isPrinting: false,
      isUpdatingData: false,
      // A filter change that arrived while a refresh was already running.
      queuedFilterUpdate: false,
      // Filter key captured for the refresh that is currently in flight.
      inflightFilterKey: null,
      currentRequestId: 0,
      // Track what triggered the last update to prevent duplicates
      lastUpdateTrigger: null,
      // Debounce timer for filter changes
      filterChangeTimer: null,
      // Track last company value to detect cascading site changes
      lastCompanyValue: null,
      // Flag to track if company change is in progress (to skip site watcher)
      isCompanyChangeInProgress: false,
      isFullScreen: false,
      // Rooms filter selection before navigating to a room (restored when clicking back)
      savedRoomsBeforeRoomView: null,
      customizeIcon: mdiTune,
      isExportMenuOpen: false,
    }
  },
  created() {
    // Only watch RTL changes, don't trigger on mount
    this.$store.watch(
      (state) => this.isRTL,
      (newValue, oldValue) => {
        // Only update if RTL actually changed (not initial mount)
        if (oldValue !== undefined && newValue !== oldValue) {
          this.updateData('rtl-change')
        }
      },
      { deep: true }
    )
  },
  mounted() {
    document.onfullscreenchange = () => {
      const fullscreenEl = document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement || document.msFullscreenElement
      this.isFullScreen = fullscreenEl === this.$refs.dashboardFullScreenTarget
      this.isOverviewShown = false
      setTimeout(() => {
        this.isOverviewShown = true
      }, 0)
    }
    // Initialize lastCompanyValue to current company value
    if (this.filterSelected && this.filterSelected.company) {
      this.lastCompanyValue = this.filterSelected.company;
    }
  },
  destroyed() {
    document.removeEventListener('click', this.closeExportMenu)
    document.onfullscreenchange = undefined
    if (this.showExportModal) unlockPageScroll()
    // Clear debounce timer if it exists
    if (this.filterChangeTimer) {
      clearTimeout(this.filterChangeTimer)
      this.filterChangeTimer = null
    }
    // Clear performance data when leaving the dashboard entirely
    this.$store.commit({ type: 'clearPerformanceData' })
    // Also reset loading state
    this.$store.commit({ type: 'toggleLoading', value: false })
  },
  methods: {
    openFullScreen() {
      const wrapper = this.$refs.dashboardFullScreenTarget
      if (!wrapper) return
      if (wrapper.requestFullscreen) wrapper.requestFullscreen()
      else if (wrapper.msRequestFullscreen) wrapper.msRequestFullscreen()
      else if (wrapper.mozRequestFullScreen) wrapper.mozRequestFullScreen()
      else if (wrapper.webkitRequestFullscreen) wrapper.webkitRequestFullscreen()
    },
    closeFullScreen() {
      const doc = document
      if (doc.exitFullscreen) doc.exitFullscreen()
      else if (doc.webkitExitFullscreen) doc.webkitExitFullscreen()
      else if (doc.mozCancelFullScreen) doc.mozCancelFullScreen()
      else if (doc.msExitFullscreen) doc.msExitFullscreen()
    },
    
    /**
     * Central data fetching method
     * @param {string} trigger - What triggered this update (for deduplication)
     */
    async updateData(trigger = 'unknown') {
      // A refresh is already running. Remember that the filter moved so the
      // in-flight request cannot leave the previous date range on screen.
      if (this.isUpdatingData) {
        this.queuedFilterUpdate = true
        return
      }
      
      // Don't refetch if the same trigger fired twice rapidly with the same filter.
      // A new date range inside that window still has to load.
      const now = Date.now()
      if (this.lastUpdateTrigger === trigger && this.lastUpdateTime && (now - this.lastUpdateTime) < 100) {
        const latestKey = normalizeAnalyticsFilter(this.$store.getters.filterSelected || {})
        if (latestKey === this.inflightFilterKey) return
      }
      
      // Set updating flag FIRST to prevent race conditions
      this.isUpdatingData = true
      
      // Increment request ID to track the current request
      const requestId = ++this.currentRequestId
      
      this.lastUpdateTrigger = trigger
      this.lastUpdateTime = now
      
      try {
        this.analyticsHideInfo = []
        
        // Always get the LATEST filterSelected from store, not from component's cached value
        // This ensures we use the current filter even if component hasn't re-rendered yet
        const latestFilterSelected = this.$store.getters.filterSelected;
        
        // Drop site-specific servik filter when company/site changes
        let filter = !this.roomId ? { ...latestFilterSelected } : { ...latestFilterSelected, roomId: this.roomId };
        
        this.inflightFilterKey = normalizeAnalyticsFilter(latestFilterSelected || {})

        // Ensure servikName is removed (not just null) if company/site changed
        if (trigger === 'company-change' || trigger === 'site-change') {
          if (filter.servikName != null) {
            // Remove servikName from filter object entirely
            const { servikName, ...filterWithoutServikName } = filter;
            filter = filterWithoutServikName;
          }
        }
        
        // Handle case where currentSiteInfo might not be loaded yet
        if (!this.currentSiteInfo) {
          await this.$store.dispatch('loadCurrentSiteInfo');
        }

        await this.tabsToShow()
        
        const { exposures } = this.currentSiteInfo || {}
        if (!exposures) this.analyticsHideInfo.push('exposures')
        if (!filter.company) {
          this.isUpdatingData = false
          return
        }
        
        this.$store.commit({ type: 'toggleLoading', value: true })
        this.isTabsLoading = true
        
        // Fetch room-specific data based on CURRENT route only
        // This ensures room-specific data is only fetched when on the appropriate route
        const routeName = this.$route.name;
        const prms = []
        if (this.roomId) {
          if (routeName === 'cleanerArrival') {
            prms.push(this.getCleanerArrivalsData(this.roomId, filter))
          } else if (routeName === 'roomExposures') {
            prms.push(this.getRoomExposuresData(this.roomId, filter))
          } else if (routeName === 'cleanLog') {
            prms.push(this.getHourTableData(this.roomId, filter, 'clean'))
          } else if (routeName === 'maintenanceLog') {
            prms.push(this.getHourTableData(this.roomId, filter, 'maintenance'))
          }
        }
        
        // Fetch analytics and overview data in parallel
        await Promise.all([
          this.$store.dispatch({ type: 'getAnalyticsData', filter }),
          this.$store.dispatch({ type: 'getOverviewData', filter }),
          ...prms,
        ])
        
        // Check if this request is still the latest one
        // For company-change and site-change, always process results even if newer requests exist
        // because these are critical updates that shouldn't be ignored
        const isCriticalUpdate = trigger === 'company-change' || trigger === 'site-change';
        
        if (!isCriticalUpdate && requestId !== this.currentRequestId) {
          // This request is stale, ignore the results
          return
        }
        
        this.$store.commit({ type: 'toggleLoading', value: false })
      } catch (error) {
        // Only handle errors if this is still the current request
        if (requestId === this.currentRequestId) {
          console.error('[Dashboard] Error updating data:', {
            timestamp: new Date().toISOString(),
            requestId: requestId,
            trigger: trigger,
            error: error,
            errorMessage: error?.message,
            errorStack: error?.stack
          });
          this.$store.commit({ type: 'toggleLoading', value: false })
          // Show user-friendly error message
          new Toast({
            message: this.$t('failed_to_load_data') || 'Failed to load dashboard data. Please try refreshing the page.',
            type: 'error',
            duration: 5000
          });
        }
      } finally {
        // Only reset flag if this is still the current request
        if (requestId === this.currentRequestId) {
          this.isUpdatingData = false
          if (this.queuedFilterUpdate) {
            this.queuedFilterUpdate = false
            const latestKey = normalizeAnalyticsFilter(this.$store.getters.filterSelected || {})
            if (latestKey !== this.inflightFilterKey) {
              this.updateData('queued-filter-change')
            }
          }
        }
      }
    },
    
    /**
     * Debounced filter update - waits a short time to allow multiple filter changes
     * to batch together before making the API call
     */
    debouncedFilterUpdate(trigger) {
      // Clear any existing timer
      if (this.filterChangeTimer) {
        clearTimeout(this.filterChangeTimer)
      }
      
      // Set a new timer - if another filter changes within 50ms, this will be cancelled
      this.filterChangeTimer = setTimeout(() => {
        this.updateData(trigger)
        this.filterChangeTimer = null
      }, 50)
    },
    
    async getCleanerArrivalsData() {
      const arrivals = await getRoomCleanerArrivals(this.roomId, this.filterSelected)
      this.cleanerArrivals = arrivals
    },
    
    async getRoomExposuresData(roomId, filter) {
      // Use provided filter or fall back to current filterSelected from store
      const filterToUse = filter || this.$store.getters.filterSelected;
      const roomIdToUse = roomId || this.roomId;
      
      // Ensure filter includes roomId and language
      const finalFilter = {
        ...filterToUse,
        roomId: roomIdToUse,
        language: this.$i18n.locale
      };
      
      let roomExposures = await getRoomExposures(roomIdToUse, finalFilter)

      roomExposures.forEach((shift) => {
        for (let i = shift.length - 1; i >= 0; i--) {
          if (i === 0) {
            const { complaintExposure, visitors } = shift[i].overview
            const complaintExposureNum = complaintExposure || 0
            const visitorsNum = visitors || 0
            const sum = complaintExposureNum + visitorsNum
            
            if (sum === 0) {
              shift[i].score = `100%`
            } else {
              const score = parseInt((visitorsNum / sum) * 100)
              shift[i].score = isNaN(score) ? `100%` : `${score}%`
            }
          }
          if (
            i - 1 >= 0 &&
            shift[i].servik_id &&
            shift[i - 1].servik_id &&
            shift[i].servik_id === shift[i - 1].servik_id &&
            new Date(Array.isArray(shift[i].endTime) ? shift[i].endTime[0] : shift[i].endTime).getTime() -
              new Date(Array.isArray(shift[i - 1].endTime) ? shift[i - 1].endTime[0] : shift[i - 1].endTime).getTime() <
              180000
          ) {
            const newItem = { ...shift[i - 1], ...shift[i] }
            newItem.visitors = shift[i - 1].visitors + shift[i].visitors
            newItem.endTime = [shift[i - 1].endTime, shift[i].endTime]
            shift.splice(i - 1, 2, newItem)
          }
        }
      })

      this.roomExposures = roomExposures
    },
    
    async getHourTableData(roomId, filter, logType) {
      const filterToUse = filter || this.$store.getters.filterSelected;
      const roomIdToUse = roomId || this.roomId;
      
      let tableData = await getRoomDataByHour(roomIdToUse, { ...filterToUse, type: logType });
      tableData = tableData.sort((a, b) => new Date(b.openTime) - new Date(a.openTime));
      this.hourTableData = tableData;
    },
    
    redirectFromHiddenExposureTab() {
      if (this.$route.name !== 'roomExposures' || !this.$route.params.roomId) return
      this.$router.replace(`/dashboard/roomAnalyticsLog/${this.$route.params.roomId}`)
    },

    updateExposureVisibility() {
      if (!this.roomId || !this.currentSiteInfo) {
        this.isExposureShown = false
        return
      }

      this.isExposureShown = !!this.currentSiteInfo.exposures
      if (!this.isExposureShown) {
        this.redirectFromHiddenExposureTab()
      }
    },

    async tabsToShow() {
      const allTabs = ['overview', 'suggestion', 'analytics', 'performance', 'communication', 'roiCalculator', 'cleaningWorkPlan']
      this.isSurveyOverview = false
      if (this.currentSiteInfo) {
        if (this.currentSiteInfo.type === 'operation') this.tabs =allTabs
        else if (this.currentSiteInfo.type === 'survey' || this.currentSiteInfo.type === 'questioner' || this.currentSiteInfo.type === 'task') {
          this.tabs = ['overview']
          this.isSurveyOverview = true
        }
      } else this.tabs = allTabs

      if (this.roomId) {
        this.updateExposureVisibility()
      } else {
        this.isExposureShown = false
      }
    },
    
    print() {
      if (this.$route.name === 'cleanerArrival') {
        this.$store.commit("setPrintCleanerArrival", true) // Trigger print
      }
      this.isPrinting = true
    },
    
    toggleExportMenu() {
      this.isExportMenuOpen = !this.isExportMenuOpen
      if (this.isExportMenuOpen) {
        this.$nextTick(() => document.addEventListener('click', this.closeExportMenu))
      } else {
        document.removeEventListener('click', this.closeExportMenu)
      }
    },
    closeExportMenu() {
      this.isExportMenuOpen = false
      document.removeEventListener('click', this.closeExportMenu)
    },
    runExport(kind) {
      this.closeExportMenu()
      if (kind === 'print') this.print()
      else if (kind === 'executive') {
        if (!this.canExportExecutiveReport) return
        this.downloadExecutiveReport()
      }
      else if (kind === 'csv') this.csvExport()
    },
    downloadExecutiveReport() {
      if (!this.canExportExecutiveReport || this.isDownloadingExecutiveReport) return
      const blockReason = this.getAnalyticsExportBlockReason()
      if (blockReason) {
        new Toast({
          message: this.$t(blockReason),
          type: 'info',
          duration: 4000,
        })
        return
      }
      if (!this.filterSelected?.site || !this.filterSelected?.time?.timeFrom || !this.filterSelected?.time?.timeTo) {
        new Toast({
          message: this.$t('analytics_export_blocked_no_data') || 'Select a site and date range first.',
          type: 'info',
          duration: 4000,
        })
        return
      }
      this.showExecutiveExportModal = true
    },
    cancelExecutiveExport() {
      this.showExecutiveExportModal = false
    },
    async confirmExecutiveExport({ selectedKpis, period }) {
      this.exportedExecutiveKpis = selectedKpis
      this.showExecutiveExportModal = false
      await this.runExecutiveReportExport(period)
    },

    async runExecutiveReportExport(period) {
      this.isDownloadingExecutiveReport = true
      this.executiveExportProgress = null
      try {
        const payload = await getExecutiveReport(this.filterSelected, this.$i18n.locale, period)
        this.executiveReportPayload = payload
      } catch (err) {
        console.error('[Dashboard] Executive report failed', err)
        this.executiveExportProgress = null
        this.isDownloadingExecutiveReport = false
        new Toast({
          message: err?.response?.data || err?.message || this.$t('data_export_failed'),
          type: 'error',
          duration: 5000,
        })
      }
    },

    onExecutiveReportProgress(progress) {
      this.executiveExportProgress = progress
    },

    onExecutiveReportDone() {
      this.executiveReportPayload = null
      this.exportedExecutiveKpis = []
      this.executiveExportProgress = null
      this.isDownloadingExecutiveReport = false
      new Toast({
        message: this.$t('data_exported_successfully') || 'Report exported successfully',
        type: 'success',
        duration: 2500,
      })
    },

    onExecutiveReportError(err) {
      this.executiveReportPayload = null
      this.exportedExecutiveKpis = []
      this.executiveExportProgress = null
      this.isDownloadingExecutiveReport = false
      new Toast({
        message: err?.message || this.$t('data_export_failed'),
        type: 'error',
        duration: 5000,
      })
    },

    buildAnalyticsExportFilterSummaryRows() {
      const f = this.$store.getters.filterSelected
      const opts = this.filterOptions
      const rows = []
      const push = (Field, Value) => {
        if (Value === undefined || Value === null || Value === '') return
        if (Array.isArray(Value) && Value.length === 0) return
        rows.push({
          Field,
          Value: Array.isArray(Value) ? Value.join('; ') : String(Value),
        })
      }
      const formatRangeDate = (d) => {
        if (d === undefined || d === null || d === '') return ''
        const t = new Date(d)
        if (Number.isNaN(t.getTime())) return String(d)
        return t.toISOString().split('T')[0]
      }
      const exportedAt = new Date()
      push('Exported at', exportedAt.toISOString())

      if (!f || !opts || !Array.isArray(opts.companies)) {
        return rows
      }

      const company = opts.companies.find((c) => c.id === f.company)
      push(this.$t('company'), company ? company.name : String(f.company))

      const sites = company ? company.sites : []
      const site = sites.find((s) => s.id === f.site)
      push(
        this.$t('site'),
        site ? site.name : this.currentSiteInfo?.name || String(f.site)
      )

      if (f.timeType) {
        const presetLabel = this.$te(f.timeType) ? this.$t(f.timeType) : f.timeType
        push('Time preset', presetLabel)
      }
      if (f.time) {
        push('Date from', formatRangeDate(f.time.timeFrom))
        push('Date to', formatRangeDate(f.time.timeTo))
      }

      const viewByKey =
        f.viewTimeBy === 'day'
          ? 'view_by_days'
          : f.viewTimeBy === 'week'
            ? 'view_by_weeks'
            : f.viewTimeBy === 'month'
              ? 'view_by_months'
              : f.viewTimeBy === 'year'
                ? 'view_by_years'
                : null
      if (viewByKey && this.$te(viewByKey)) {
        push('View by', this.$t(viewByKey))
      } else if (f.viewTimeBy) {
        push('View by', f.viewTimeBy)
      }

      const building = f.building
      if (building) {
        const b = Array.isArray(building) ? building.join('; ') : String(building)
        push(this.$t('building'), b)
      }
      if (f.floor && f.floor.length) {
        push(this.$t('floor'), f.floor.join('; '))
      }
      if (f.gender && f.gender.length) {
        push(this.$t('gender'), f.gender.map((g) => this.$roomTypeLabel(g)).join('; '))
      }
      if (f.shift && f.shift.length && Array.isArray(opts.shift)) {
        const names = f.shift
          .map((id) => {
            const opt = opts.shift.find((s) => s.id === id)
            return opt ? opt.name : String(id)
          })
          .filter(Boolean)
        push(this.$t('shift'), names.join('; '))
      }
      if (f.days && f.days.length) {
        push(this.$t('days'), f.days.map(String).join('; '))
      }
      if (f.rooms && f.rooms.length) {
        const names = f.rooms
          .map((id) => {
            const r = this.roomIdMap[id]
            return r ? r.name : String(id)
          })
          .filter(Boolean)
        push(this.$t('rooms'), names.join('; '))
      }
      if (hasServikNameFilter(f.servikName)) {
        const names = getServikDisplayNames(
          f.servikName,
          this.$store.getters.filterOptions?.servikName || [],
        )
        push(this.$t('servikName'), names.join('; '))
      }
      if (f.hour !== undefined && f.hour !== null && f.hour !== '') {
        push(this.$t('hour'), String(f.hour))
      }
      return rows
    },

    getAnalyticsExportBlockReason() {
      if (this.isExportingCsv) return 'analytics_export_blocked_loading'
      if (this.isLoading || this.isUpdatingData) return 'analytics_export_blocked_loading'
      if (this.isAdditionalFilterOpen) return 'analytics_export_blocked_stale_filters'
      if (!this.analyticsData || !this.barItemsData || !this.chartListData) {
        return 'analytics_export_blocked_no_data'
      }
      const currentKey = normalizeAnalyticsFilter(this.filterSelected || {})
      const loadedKey = this.loadedAnalyticsFilterKey
      if (!loadedKey || currentKey !== loadedKey) {
        return 'analytics_export_blocked_stale_filters'
      }
      return null
    },

    buildAnalyticsExportZipFilename() {
      const siteName = (this.currentSiteInfo?.name || 'Site').replace(/[/\\:*?"<>|]/g, '-').trim() || 'Site'
      const formatRangeDate = (d) => {
        if (d === undefined || d === null || d === '') return ''
        const t = new Date(d)
        if (Number.isNaN(t.getTime())) return String(d)
        return t.toISOString().split('T')[0]
      }
      const from = formatRangeDate(this.filterSelected?.time?.timeFrom)
      const to = formatRangeDate(this.filterSelected?.time?.timeTo)
      const rangePart = from && to ? ` ${from} to ${to}` : ''
      return `${siteName} - analytics${rangePart}.zip`
    },

    csvExport() {
      if (this.isExportingCsv) return

      const blockReason = this.getAnalyticsExportBlockReason()
      if (blockReason) {
        new Toast({
          message: this.$t(blockReason),
          type: 'info',
          duration: 4000,
        })
        return
      }

      this.showExportModal = true
    },

    cancelExport() {
      if (this.isExportingCsv) return
      this.showExportModal = false
    },

    async confirmExport() {
      if (this.isExportingCsv) return

      const blockReason = this.getAnalyticsExportBlockReason()
      if (blockReason) {
        new Toast({
          message: this.$t(blockReason),
          type: 'info',
          duration: 4000,
        })
        this.showExportModal = false
        return
      }

      this.isExportingCsv = true
      try {
        const filterSelected = this.filterSelected
        const siteId = filterSelected.site
        const arrivalQuery = buildAnalyticsArrivalQuery(filterSelected, { language: this.$i18n.locale })

        // Per-room arrivals: hour columns from the stored hour_map (same source as the panel hour table),
        // per-room totals from the shift-wide count so the sheet totals match the panel KPI and room.csv.
        const {
          cleaner: cleanerArrivalData,
          supervisor: supervisorArrivalData,
          cleanerTotals,
          supervisorTotals,
        } = await performanceService.getSiteArrivalsByHour(siteId, arrivalQuery)
        const cleanersArrivalCsv = csvService.generateArrivalByHourCsv(
          cleanerArrivalData,
          cleanerTotals,
          this.roomIdMap
        )
        const supervisorsArrivalCsv = csvService.generateArrivalByHourCsv(
          supervisorArrivalData,
          supervisorTotals,
          this.roomIdMap
        )

        const mainBarRows = []
        for (const name in this.barItemsData) {
          mainBarRows.push({
            name: this.$t(name),
            value: this.barItemsData[name].value,
            trend: this.barItemsData[name].trendInfo,
          })
        }
        const mainCsv = csvService.generateMainBarCsv(mainBarRows)
        const donutCsv = csvService.getCsvForChartListData(this.chartListData)
        const hourCsv = csvService.generateHourCsv(this.analyticsData.hourData)
        const roomCsv = csvService.generateRoomCsv(this.analyticsData.roomsData)
        const filterSummaryCsv = csvService.generateFilterSummaryCsv(this.buildAnalyticsExportFilterSummaryRows())
        const zipFileName = this.buildAnalyticsExportZipFilename()

        await zipService.generateFolder(
          'csvFolder',
          zipFileName,
          { name: 'filters.csv', file: filterSummaryCsv },
          { name: 'main.csv', file: mainCsv },
          { name: 'donut.csv', file: donutCsv },
          { name: 'hour.csv', file: hourCsv },
          { name: 'room.csv', file: roomCsv },
          { name: 'cleaners-arrivals.csv', file: cleanersArrivalCsv },
          { name: 'supervisors-arrivals.csv', file: supervisorsArrivalCsv }
        )

        this.showExportModal = false
        new Toast({
          message: this.$t('data_exported_successfully'),
          type: 'success',
          duration: 2500,
        })
      } catch (err) {
        console.error('[Dashboard] CSV export failed', err)
        new Toast({
          message: this.$t('data_export_failed'),
          type: 'error',
          duration: 5000,
        })
      } finally {
        this.isExportingCsv = false
      }
    },
    
    resetData() {
      this.cleanerArrivals = []
      this.roomExposures = []
    },
    enterDashboardEditMode() {
      this.$store.commit('setDashboardEditMode', true)
    },
  },
  computed: {
    ...mapGetters([
      'isRTL',
      'filterSelected',
      'isLoading',
      'analyticsData',
      'barItemsData',
      'chartListData',
      'roomIdMap',
      'loggedUser',
      'filterOptions',
      'currentSiteInfo',
      'printingDone',
      'filterJustApplied',
      'loadedAnalyticsFilterKey',
      'isAdditionalFilterOpen',
      'isDashboardEditMode',
    ]),
    roomId() {
      return this.$route.params.roomId
    },
    isAdmin() {
      return this.loggedUser.type === 'admin'
    },
    canExportExecutiveReport() {
      return canExportExecutiveReport(this.loggedUser)
    },
    singlePanelShiftId() {
      const selected = this.filterSelected && this.filterSelected.shift
      if (!Array.isArray(selected) || selected.length !== 1) return ''
      return selected[0]
    },
    siteInfo() {
      return this.currentSiteInfo
    },
    showPDFExport() {
      const routes = ['analytics', 'cleanerArrival', 'roomAnalyticsLog']
      return routes.includes(this.$route.name)
    },
    showDashboardCustomizeButton() {
      return (
        DASHBOARD_CUSTOMIZE_ENABLED &&
        this.$route.name === 'overview' &&
        !this.isFullScreen &&
        !this.isDashboardEditMode
      )
    },
    showOverviewTabActions() {
      return this.$route.name === 'overview' && !this.isFullScreen
    },
    showAnalyticsTabActions() {
      return this.$route.name === 'analytics'
    },
    executiveExportStatusDetail() {
      const progress = this.executiveExportProgress
      if (progress && progress.stage === 'capture' && progress.total) {
        return this.$t('er_export_status_page', { page: progress.page, total: progress.total })
      }
      if (progress && progress.stage === 'layout') return this.$t('er_export_status_layout')
      return this.$t('er_export_status_fetch')
    },
    isExportingAnything() {
      return (
        this.isPrinting ||
        this.isDownloadingExecutiveReport ||
        this.isExportingCsv
      )
    },
    cleaningWorkPlanTabLabel() {
      return this.$t('cleaning_work_plan');
    },
  },
  watch: {
    showExportModal(isVisible) {
      if (isVisible) lockPageScroll()
      else unlockPageScroll()
    },
    // Watch siteInfo with immediate to trigger initial load
    siteInfo: {
      deep: true,
      immediate: true,
      handler: function(newValue, oldValue) {
        // Only update if we're actually on a dashboard route
        if (!this.$route.path.startsWith('/dashboard')) return
        
        // Don't trigger if company change is in progress - company watcher will handle it
        if (this.isCompanyChangeInProgress) {
          return;
        }
        
        if (newValue) {
          // Only trigger if this is initial load OR if siteInfo actually changed
          if (!oldValue || oldValue !== newValue) {
            this.updateData('siteInfo-change')
          }
        }
      },
    },
    
    // Watch roomId changes (navigation between room detail pages)
    roomId(newValue, oldValue) {
      // Only update if we're actually on a dashboard route
      if (!this.$route.path.startsWith('/dashboard')) return
      
      // Only update if roomId actually changed (not just component remount)
      if (newValue !== oldValue) {
        if (newValue) {
          this.isExposureShown = false
          // Navigating TO a room: save current room filter so we can restore it on back
          const currentRooms = this.$store.getters.filterSelected?.rooms
          this.savedRoomsBeforeRoomView = Array.isArray(currentRooms) ? [...currentRooms] : currentRooms
        } else {
          // Navigating BACK from room: restore previous room selection instead of clearing
          this.$store.commit({ type: 'updateFilter', field: 'servikName', value: null })
          this.$store.commit({
            type: 'updateFilter',
            field: 'rooms',
            value: this.savedRoomsBeforeRoomView !== undefined ? this.savedRoomsBeforeRoomView : null,
          })
          this.savedRoomsBeforeRoomView = null
        }
        this.resetData()
        this.updateData('roomId-change')
      }
    },
    
    // Watch route name changes for room-specific data
    '$route.name': function(currValue, oldValue) {
      if (currValue !== oldValue) this.closeExportMenu()
      // Only fetch room-specific data when route changes
      if (currValue !== oldValue && this.roomId) {
        // Always use latest filter from store (includes applied filters)
        const latestFilter = this.$store.getters.filterSelected;
        const filter = {
          ...latestFilter,
          roomId: this.roomId,
          language: this.$i18n.locale
        };
        
        // Always reload room-specific data when navigating to these routes
        // This ensures filters are applied even if navigating from another tab
        if (currValue === 'cleanerArrival') {
          this.getCleanerArrivalsData(this.roomId, filter)
        } else if (currValue === 'roomExposures') {
          this.getRoomExposuresData(this.roomId, filter)
        } else if (currValue === 'cleanLog') {
          this.getHourTableData(this.roomId, filter, 'clean')
        } else if (currValue === 'maintenanceLog') {
          this.getHourTableData(this.roomId, filter, 'maintenance')
        }
      }
    },
    
    // Watch company changes - requires full reload
    'filterSelected.company': {
      deep: true,
      immediate: false,
      handler: async function(newValue, oldValue) {
        // Only update if we're actually on a dashboard route
        if (!this.$route.path.startsWith('/dashboard')) return
        
        // Don't trigger if filter modal is open - wait for Apply Filter button
        if (this.$store.getters.isAdditionalFilterOpen) return
        
        // Only update if company actually changed
        if (newValue && oldValue && newValue !== oldValue) {
          // Set flag to prevent site watcher from firing (company change cascades to site change)
          this.isCompanyChangeInProgress = true;
          // Store the new company value
          this.lastCompanyValue = newValue;
          // Clear data immediately when company changes to prevent showing stale data
          this.$store.commit({ type: 'clearPerformanceData' })
          try {
            await this.updateData('company-change');
          } finally {
            // Clear flag after update completes (with small delay to ensure site watcher has checked)
            setTimeout(() => {
              this.isCompanyChangeInProgress = false;
            }, 200);
          }
        }
      },
    },
    
    // Watch site changes - requires full reload
    'filterSelected.site': {
      deep: true,
      immediate: false,
      handler: function(newValue, oldValue) {
        // Don't trigger if filter modal is open - wait for Apply Filter button
        if (this.$store.getters.isAdditionalFilterOpen) return
        
        // Only update if site actually changed
        if (newValue && oldValue && newValue !== oldValue) {
          // If company change is in progress, skip site watcher - company watcher will handle it
          // This prevents duplicate API calls when company change cascades to site change
          if (this.isCompanyChangeInProgress) {
            return;
          }
          // Clear data immediately when site changes to prevent showing stale data
          this.$store.commit({ type: 'clearPerformanceData' })
          
          // Only redirect if currently on a room-specific route
          // Don't redirect if navigating away from dashboard
          if (this.$route.params.roomId && this.$route.path.startsWith('/dashboard')) {
            this.$router.push('/dashboard/analytics')
          }
          
          this.updateData('site-change')
        }
      },
    },
    
    // Watch other filter changes (time, etc.)
    'filterSelected.time': {
      deep: true,
      immediate: false,
      handler: function(newValue, oldValue) {
        // Only update if we're actually on a dashboard route
        if (!this.$route.path.startsWith('/dashboard')) return

        // Don't trigger if filter modal is open - wait for Apply Filter button
        if (this.$store.getters.isAdditionalFilterOpen) return

        // For "Choose dates" (custom_time) only skip refetch while user is picking dates in the modal.
        // When they click Apply, filterJustApplied watcher runs updateData. When we come from
        // Performance (date bar click), modal is closed so we must refetch here.
        if (this.filterSelected.timeType === 'custom_time' && this.$store.getters.isAdditionalFilterOpen) return

        // Only update if time filter actually changed
        if (newValue && oldValue && JSON.stringify(newValue) !== JSON.stringify(oldValue)) {
          this.updateData('time-filter-change')
        }
      },
    },
    
    // Watch servikName changes (from chart clicks)
    'filterSelected.servikName': {
      deep: true,
      immediate: false,
      handler: function(newValue, oldValue) {
        // Only update if we're actually on a dashboard route
        if (!this.$route.path.startsWith('/dashboard')) return
        
        // Don't trigger if filter modal is open - wait for Apply Filter button
        if (this.$store.getters.isAdditionalFilterOpen) return
        
        // Don't trigger if we're restoring filter state (user closed modal without applying)
        if (this.$store.getters.isRestoringFilterState) return
        
        // Only update if servikName actually changed
        if (newValue !== oldValue) {
          // Debounce to allow both servikName and rooms to update together
          this.debouncedFilterUpdate('servikName-filter-change')
        }
      },
    },
    
    // Watch rooms changes (from chart clicks or filter selections)
    'filterSelected.rooms': {
      deep: true,
      immediate: false,
      handler: function(newValue, oldValue) {
        // Only update if we're actually on a dashboard route
        if (!this.$route.path.startsWith('/dashboard')) return
        
        // Don't trigger if filter modal is open - wait for Apply Filter button
        if (this.$store.getters.isAdditionalFilterOpen) return
        
        // Don't trigger if we're restoring filter state (user closed modal without applying)
        if (this.$store.getters.isRestoringFilterState) return
        
        // Only update if rooms actually changed
        if (JSON.stringify(newValue) !== JSON.stringify(oldValue)) {
          // Debounce to allow both servikName and rooms to update together
          this.debouncedFilterUpdate('rooms-filter-change')
        }
      },
    },
    
    // Watch shift changes (from filter selections)
    'filterSelected.shift': {
      deep: true,
      immediate: false,
      handler: function(newValue, oldValue) {
        // Only update if we're actually on a dashboard route
        if (!this.$route.path.startsWith('/dashboard')) return
        
        // Don't trigger if filter modal is open - wait for Apply Filter button
        if (this.$store.getters.isAdditionalFilterOpen) return
        
        // Don't trigger if we're restoring filter state (user closed modal without applying)
        if (this.$store.getters.isRestoringFilterState) return
        
        // Only update if shift actually changed
        if (JSON.stringify(newValue) !== JSON.stringify(oldValue)) {
          // Debounce to allow multiple filter changes to batch together
          this.debouncedFilterUpdate('shift-filter-change')
        }
      },
    },
    
    'filterSelected.cleaners': {
      deep: true,
      immediate: false,
      handler: function(newValue, oldValue) {
        if (!this.$route.path.startsWith('/dashboard')) return
        if (this.$store.getters.isAdditionalFilterOpen) return
        if (this.$store.getters.isRestoringFilterState) return
        if (JSON.stringify(newValue) !== JSON.stringify(oldValue)) {
          this.debouncedFilterUpdate('cleaners-filter-change')
        }
      },
    },

    // Watch days changes (from filter selections)
    'filterSelected.days': {
      deep: true,
      immediate: false,
      handler: function(newValue, oldValue) {
        // Only update if we're actually on a dashboard route
        if (!this.$route.path.startsWith('/dashboard')) return
        
        // Don't trigger if filter modal is open - wait for Apply Filter button
        if (this.$store.getters.isAdditionalFilterOpen) return
        
        // Don't trigger if we're restoring filter state (user closed modal without applying)
        if (this.$store.getters.isRestoringFilterState) return
        
        // Only update if days actually changed
        if (JSON.stringify(newValue) !== JSON.stringify(oldValue)) {
          // Debounce to allow multiple filter changes to batch together
          this.debouncedFilterUpdate('days-filter-change')
        }
      },
    },
    
    // Watch isActiveFilter to detect when filters are applied
    // This ensures room-specific data is fetched even if shift/days values didn't change
    'isActiveFilter': {
      immediate: false,
      handler: function(newValue, oldValue) {
        // Only update if we're actually on a dashboard route with a roomId
        if (!this.$route.path.startsWith('/dashboard') || !this.roomId) return
        
        // Don't trigger if filter modal is open
        if (this.$store.getters.isAdditionalFilterOpen) return
        
        // Don't trigger if we're restoring filter state
        if (this.$store.getters.isRestoringFilterState) return
        
        // Only trigger when filter becomes active (was applied)
        // This handles the case where shift/days were already in the store
        // so the shift/days watchers didn't fire
        if (newValue && newValue !== oldValue) {
          const routeName = this.$route.name;
          // Only update if we're on a route that uses shift/days filters
          // Only trigger for the current route, not all room routes
          if (routeName === 'roomExposures' || routeName === 'cleanLog' || routeName === 'maintenanceLog' || routeName === 'cleanerArrival') {
            // Use debounce to batch with other filter changes
            this.debouncedFilterUpdate('filter-applied')
          }
        }
      },
    },
    
    // Watch filterJustApplied flag to trigger update when filters are applied
    // This handles the case where filter values didn't change (were already in store)
    // but user clicked "Apply" button
    'filterJustApplied': {
      immediate: false,
      handler: function(newValue, oldValue) {
        // Only trigger if flag becomes true (filter was just applied)
        if (newValue && !oldValue) {
          // Only update if we're on a dashboard route
          if (!this.$route.path.startsWith('/dashboard')) return
          
          // Don't trigger if filter modal is open
          if (this.$store.getters.isAdditionalFilterOpen) return
          
          // Don't trigger if we're restoring filter state
          if (this.$store.getters.isRestoringFilterState) return
          
          const routeName = this.$route.name;
          
          // Only trigger updateData for the current route
          // updateData will check the route internally and only fetch room-specific data for that route
          if (this.roomId && (routeName === 'roomExposures' || routeName === 'cleanLog' || routeName === 'maintenanceLog' || routeName === 'cleanerArrival')) {
            // Only trigger if we're on the specific route that needs the data
            // updateData will handle fetching the correct room-specific data based on routeName
            this.debouncedFilterUpdate('filter-just-applied')
          } else if (!this.roomId) {
            // For non-room routes, also trigger update
            this.debouncedFilterUpdate('filter-just-applied')
          }
        }
      },
    },
    
    printingDone(newVal) {
      if (newVal) {
        this.isPrinting = false
      }
    },
  },
  components: {
    Tabs,
    Tab,
    Performance,
    Overview,
    Analytics,
    ServiceLog,
    Suggestion,
    Communication,
    Loading,
    SurveyOverview,
    roiCalculator,
    CleaningWorkPlan,
    ExecutiveReportExportDialog,
    ExecutiveReportPrint,
  },
}
</script>

<style lang="scss">
@use "@/styles/mixins" as *;
@mixin top-button {
  position: absolute;
  display: flex;
  align-items: center;
  height: $tabs-height;
  z-index: 11;
}

.overview-analytics-container {
  background-color: white;
  min-height: 100%;

  .iframe-tab {
    .md-content {
      height: 75vh !important;
      .md-tabs-container {
        height: 100%;
      }
    }
    .overview-tab {
      height: 100%;
      .iframe-section {
        height: 100%;
        width: 100%;
        @include shadow-border;
        padding: 10px;
        margin-top: 10px;
        iframe {
          width: 100%;
          height: 100%;
        }
      }
    }
  }

  .dashboard-tabs--cleaning-work-plan {
    .md-content.md-tabs-content,
    .md-tabs-container,
    .md-tab {
      height: auto !important;
      min-height: 0;
      overflow: visible !important;
    }
  }

  .dashboard-fullscreen-wrapper {
    position: relative;
  }

  .dashboard-fullscreen-wrapper:fullscreen,
  .dashboard-fullscreen-wrapper:-webkit-full-screen,
  .dashboard-fullscreen-wrapper:-moz-full-screen,
  .dashboard-fullscreen-wrapper:-ms-fullscreen {
    background-color: $white;
    .overview-container {
      height: 98vh;
    }
    .overview-container--edit {
      max-height: 98vh;
    }
  }

  .exit-full-screen-button {
    @include top-button;
    right: 16px;
    padding: 0;
    background: transparent;
    border: none;
    cursor: pointer;
    @include rtl {
      left: 16px;
      right: unset;
    }
    img {
      display: block;
      width: 46px;
      height: 46px;
    }
  }

  @media print {
    min-height: unset;
  }

  // Keep customize + fullscreen as one cluster so they never sit on top of the
  // last tab label (e.g. Cleaning Work Plan) when the tab bar is crowded.
  .dashboard-tab-actions {
    @include top-button;
    right: 12px;
    gap: 4px;
    @include rtl {
      left: 12px;
      right: unset;
    }
    @media print {
      display: none;
    }
    @include screen-size(xs) {
      display: none;
    }
  }

  .full-screen-button {
    display: flex;
    align-items: center;
    padding: 0;
    border: none;
    background: transparent;
    cursor: pointer;

    img {
      display: block;
      width: 46px;
      height: 46px;
    }
  }

  .dashboard-customize-button {
    display: flex;
    align-items: center;
    padding: 0 6px;
    border: none;
    background: transparent;
    color: #4b5563;
    cursor: pointer;

    &:hover {
      color: #389ceb;
    }

    &__icon {
      display: block;
      width: 28px;
      height: 28px;
    }
  }

  &--with-tab-actions {
    .dashboard-tabs {
      .md-tabs-navigation {
        // Reserve room for the action cluster so the last tab truncates/scrolls
        // instead of colliding with the icons.
        padding-inline-end: 110px;
      }
    }
  }

  &--with-analytics-exports {
    .dashboard-tabs {
      .md-tabs-navigation {
        padding-inline-end: 72px;
      }
    }
  }

  @include screen-size(xs) {
    &--with-tab-actions,
    &--with-analytics-exports {
      .dashboard-tabs {
        .md-tabs-navigation {
          padding-inline-end: 12px;
        }
      }
    }
  }

  .export-button-container {
    @include top-button;
    right: 20px;
    @include rtl {
      left: 20px;
      right: unset;
    }

    @media print {
      display: none;
    }

    @include screen-size(xs) {
      display: none;
    }
  }

  .analytics-export {
    @include top-button;
    right: 16px;
    @include rtl {
      left: 16px;
      right: unset;
    }

    @media print {
      display: none;
    }

    @include screen-size(xs) {
      display: none;
    }

    &__trigger {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 36px;
      height: 36px;
      margin-block: auto;
      padding: 0;
      border: none;
      border-radius: 8px;
      background: transparent;
      color: #4b5563;
      cursor: pointer;

      &:hover,
      &[aria-expanded='true'] {
        color: #0b3b5c;
        background: #f1f5f9;
      }
    }

    &__icon {
      display: block;
      width: 22px;
      height: 22px;
    }

    &__menu {
      position: absolute;
      top: calc(100% - 6px);
      inset-inline-end: 0;
      min-width: 188px;
      padding: 6px;
      border: 1px solid #e2e8f0;
      border-radius: 10px;
      background: #fff;
      box-shadow: 0 10px 28px rgba(15, 23, 42, 0.12);
      z-index: 20;

      button {
        display: block;
        width: 100%;
        padding: 8px 10px;
        border: none;
        border-radius: 7px;
        background: transparent;
        color: #0f172a;
        font-size: 13px;
        line-height: 1.3;
        text-align: start;
        cursor: pointer;

        &:hover:not(:disabled) {
          background: #f8fafc;
        }

        &:disabled {
          color: #94a3b8;
          cursor: not-allowed;
        }
      }
    }
  }

  .help-tour-button {
    @include top-button;
    right: 15px;
    @include rtl {
      right: unset;
      left: 15px;
    }
    color: #64748b;
    transition: all 0.2s ease;

    &:hover {
      color: #3b82f6;
      background-color: #eff6ff;
    }

    svg {
      width: 20px;
      height: 20px;
    }

    @include screen-size(xs) {
      display: none;
    }
  }

  .bounce {
    animation: bounce 2s cubic-bezier(0.5, 1.6, 0.5, 0.7) infinite;
  }

  @keyframes bounce {
    0%,
    100% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(-20px);
    }
  }

  // Disabled tabs styling
  .md-tabs .md-tab.md-disabled {
    opacity: 0.5;
    cursor: not-allowed;
    pointer-events: none;

    .md-tab-label {
      color: #9e9e9e !important;
    }
  }

  &.sticky {
    @include sticky-tabs;
  }
}

.export-modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(15, 23, 42, 0.45);
  backdrop-filter: blur(2px);
  padding: 20px;
  overflow: auto;
  overscroll-behavior: contain;

  &.rtl {
    direction: rtl;
  }
}

.export-modal {
  position: relative;
  width: 100%;
  max-width: 420px;
  background: #ffffff;
  border-radius: 16px;
  padding: 32px 28px 24px;
  box-shadow: 0 20px 60px rgba(15, 23, 42, 0.25);
  text-align: center;
  font-family: Ubuntu, sans-serif;

  &__close {
    position: absolute;
    top: 12px;
    inset-inline-end: 16px;
    background: transparent;
    border: none;
    font-size: 26px;
    line-height: 1;
    color: #95989a;
    cursor: pointer;
    transition: color 0.15s ease;

    &:hover:not(:disabled) {
      color: #4b5563;
    }

    &:disabled {
      cursor: default;
      opacity: 0.5;
    }
  }

  &__icon {
    width: 64px;
    height: 64px;
    margin: 0 auto 18px;
    border-radius: 50%;
    background: rgba(56, 156, 235, 0.12);
    display: flex;
    align-items: center;
    justify-content: center;

    img {
      width: 30px;
      height: 30px;
    }
  }

  &__title {
    margin: 0 0 10px;
    font-size: 20px;
    font-weight: 700;
    color: #1f2a44;
  }

  &__desc {
    margin: 0 0 26px;
    font-size: 14px;
    line-height: 1.5;
    color: #6b7280;
  }

  &--kpis {
    display: flex;
    flex-direction: column;
    max-width: 560px;
    max-height: calc(100vh - 40px);
    padding: 0;
    overflow: hidden;
    text-align: start;

    .export-modal__title {
      margin: 0 0 6px;
      padding-inline-end: 28px;
    }

    .export-modal__actions {
      display: block;
      flex-shrink: 0;
      padding: 14px 24px 20px;
      border-top: 1px solid #eef2f6;
      background: #fff;
    }
  }

  &__head {
    position: relative;
    flex-shrink: 0;
    padding: 22px 24px 8px;
  }

  &__lead {
    margin: 0 0 12px;
    font-size: 13px;
    line-height: 1.45;
    color: #6b7280;
  }

  &__sublabel {
    margin: 2px 0 6px;
    font-size: 12px;
    font-weight: 600;
    color: #6b7280;
  }

  &__body {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    overscroll-behavior: contain;
    touch-action: pan-y;
    padding: 8px 24px 4px;
  }

  &__kpis-head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
    margin: 0 0 6px;

    .export-modal__section-title {
      margin: 0;
    }
  }

  &__count {
    margin-inline-start: 8px;
    font-weight: 600;
    letter-spacing: 0;
    text-transform: none;
    color: #389ceb;
  }

  &__link {
    background: none;
    border: none;
    padding: 0;
    color: #389ceb;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }

    &:disabled {
      opacity: 0.45;
      cursor: default;
      text-decoration: none;
    }
  }

  &__kpi-group + &__kpi-group {
    margin-top: 14px;
  }

  &__checks {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  &__chip {
    display: flex;
    align-items: center;
    gap: 10px;
    margin: 0;
    min-height: 40px;
    padding: 8px 12px;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    background: #fff;
    font-size: 13px;
    font-weight: 600;
    line-height: 1.3;
    color: #6b7280;
    cursor: pointer;

    input {
      width: 15px;
      height: 15px;
      margin: 0;
      flex-shrink: 0;
      accent-color: #389ceb;
      cursor: pointer;
    }

    &.is-on {
      border-color: #b9ddf8;
      background: #f3f9fe;
      color: #0b3b5c;
    }
  }

  &__period-note {
    margin: 10px 0 0;
    font-size: 12px;
    line-height: 1.45;
    color: #6b7280;
  }

  &__section {
    margin: 0 0 18px;
    padding: 14px 16px;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    background: #f9fafb;
    text-align: start;
  }

  &__section-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin: 0 0 10px;
  }

  &__section-title {
    margin: 0 0 12px;
    font-size: 13px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #6b7280;
  }

  &__section-head .export-modal__section-title {
    margin: 0;
  }

  &__hint {
    margin: 0;
    font-size: 13px;
    line-height: 1.45;
    color: #6b7280;
  }

  &__segments {
    display: flex;
    padding: 3px;
    margin: 0 0 12px;
    border-radius: 10px;
    background: #eef2f6;
  }

  &__segments--inline {
    margin: 0;
    min-width: 148px;
    flex: 0 0 auto;
  }

  &__compare-toggle {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin: 0 0 12px;

    .export-modal__section-title {
      margin: 0;
      min-width: 0;
      line-height: 1.3;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__seg {
    flex: 1;
    height: 34px;
    border: none;
    border-radius: 8px;
    background: transparent;
    font-size: 13px;
    font-weight: 600;
    color: #6b7280;
    cursor: pointer;

    &.is-active {
      background: #fff;
      color: #1f2a44;
      box-shadow: 0 1px 2px rgba(15, 23, 42, 0.08);
    }

    &:disabled {
      opacity: 0.45;
      cursor: not-allowed;
    }
  }

  &__range {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }

  &__compare-value {
    display: flex;
    align-items: center;
    min-height: 38px;
    margin: 0;
    padding: 8px 10px;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    background: #f8fafc;
    font-size: 14px;
    line-height: 1.35;
    color: #1f2a44;
  }

  &__field {
    display: flex;
    flex-direction: column;
    gap: 6px;
    font-size: 13px;
    color: #6b7280;

    select {
      height: 38px;
      padding: 0 10px;
      border: 1px solid #d1d5db;
      border-radius: 8px;
      background: #fff;
      font-size: 14px;
      color: #1f2a44;
      cursor: pointer;
      direction: inherit;
      text-align: start;

      &:disabled {
        opacity: 0.65;
        cursor: default;
      }
    }
  }

  &__presets {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 12px;
  }

  &__preset {
    padding: 6px 12px;
    border: 1px solid #d1d5db;
    border-radius: 999px;
    background: #fff;
    font-size: 12px;
    font-weight: 600;
    color: #374151;
    cursor: pointer;

    &.is-active {
      border-color: #389ceb;
      background: #e8f3fd;
      color: #0b3b5c;
    }

    &:disabled {
      opacity: 0.45;
      cursor: not-allowed;
    }
  }

  &__summary {
    margin: 0 0 10px;
    font-size: 12px;
    line-height: 1.4;
    color: #6b7280;
    text-align: center;
  }

  &__action-row {
    display: flex;
    gap: 12px;
    justify-content: center;

    .export-modal__btn--primary {
      flex: 1.6;
      max-width: none;
    }
  }

  &__actions {
    display: flex;
    gap: 12px;
    justify-content: center;
  }

  &__btn {
    flex: 1;
    max-width: 160px;
    height: 44px;
    border-radius: 10px;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s ease, border-color 0.15s ease, opacity 0.15s ease;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;

    &:disabled {
      cursor: default;
      opacity: 0.7;
    }

    &--ghost {
      background: #ffffff;
      border: 1px solid #d6dae1;
      color: #4b5563;

      &:hover:not(:disabled) {
        background: #f3f4f6;
      }
    }

    &--primary {
      background: #389ceb;
      border: 1px solid #389ceb;
      color: #ffffff;

      &:hover:not(:disabled) {
        background: #2d8cd9;
        border-color: #2d8cd9;
      }
    }
  }

  &__spinner {
    width: 16px;
    height: 16px;
    border: 2px solid rgba(255, 255, 255, 0.5);
    border-top-color: #ffffff;
    border-radius: 50%;
    animation: export-modal-spin 0.7s linear infinite;
  }
}

@keyframes export-modal-spin {
  to {
    transform: rotate(360deg);
  }
}

.export-modal-fade-enter-active,
.export-modal-fade-leave-active {
  transition: opacity 0.2s ease;

  .export-modal {
    transition: transform 0.2s ease, opacity 0.2s ease;
  }
}

.export-modal-fade-enter,
.export-modal-fade-leave-to {
  opacity: 0;

  .export-modal {
    transform: translateY(12px) scale(0.97);
    opacity: 0;
  }
}

.er-export-status {
  position: fixed;
  z-index: 2000;
  inset-inline-end: 20px;
  /* Clears the Ask Buzzz button, which is fixed 20px from the bottom. */
  bottom: 88px;
  display: flex;
  align-items: center;
  gap: 12px;
  max-width: min(340px, calc(100vw - 32px));
  padding: 12px 14px;
  background: #fff;
  color: #0f172a;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.16);
  pointer-events: none;

  &__spinner {
    flex: 0 0 auto;
    width: 18px;
    height: 18px;
    border: 2px solid #dbe4ee;
    border-top-color: #0b3b5c;
    border-radius: 50%;
    animation: export-modal-spin 0.7s linear infinite;
  }

  &__copy {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  &__title {
    font-size: 13px;
    font-weight: 700;
    line-height: 1.3;
  }

  &__detail {
    font-size: 12px;
    font-weight: 500;
    line-height: 1.3;
    color: #64748b;
  }
}

.executive-report-offscreen {
  position: fixed;
  left: -9999px;
  top: 0;
  width: 1050px;
  /* Must stay visible — html2canvas skips visibility:hidden nodes and produces a blank PDF */
  visibility: visible;
  opacity: 1;
  pointer-events: none;
  z-index: -1;
  background: #fff;
}
</style>
